import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Modal from "components/modal/modal";
import FormInput from "components/input/form-input";
import FormSelect from "components/input/form-select";
import FormTextarea from "components/input/form-textarea";
import FormSuccess from "components/modal/form-success";
import { FORMSPREE_KEYS, formspreeUrl } from "config/formspree";
import { useModal } from "context/modal-context";
import { programmes } from "data/programmes";

const cohortOptions = programmes.map((p) => `${p.title} — ${p.month}`);

const schema = yup.object({
  fullName: yup.string().required("Full name is required"),
  jobTitle: yup.string().required("Job title is required"),
  company: yup.string(),
  email: yup.string().email("Enter a valid email").required("Email is required"),
  phone: yup.string().required("Phone number is required"),
  cohort: yup.string(),
  additional: yup.string(),
});

type FormValues = yup.InferType<typeof schema>;

interface Props {
  open: boolean;
  handleClose: () => void;
}

const IndividualTrainingModal = ({ open, handleClose }: Props) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { modalPayload } = useModal();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema as any),
  });

  useEffect(() => {
    if (open && modalPayload?.programme) {
      const match = cohortOptions.find((c) => c.startsWith(modalPayload.programme as string));
      if (match) setValue("cohort", match);
    }
  }, [open, modalPayload, setValue]);

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    setSubmitError(null);
    try {
      const res = await fetch(formspreeUrl(FORMSPREE_KEYS.individualTraining), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          fullName: data.fullName,
          jobTitle: data.jobTitle,
          companyName: data.company,
          email: data.email,
          phone: data.phone,
          preferredCohort: data.cohort,
          additionalInformation: data.additional,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
      reset();
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    reset();
    setSubmitted(false);
    setSubmitError(null);
    handleClose();
  };

  return (
    <Modal open={open} handleClose={onClose} className="w-[560px] lg:p-8 p-6">
      {submitted ? (
        <FormSuccess
          title="Request Sent!"
          message="Thank you! We've received your training request and will be in touch shortly."
          onClose={onClose}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
          <div className="flex flex-col gap-1 pr-8">
            <h2 className="text-[28px] font-bold text-[#1A1A2E] leading-tight">
              Request for Individual Training
            </h2>
            <p className="text-sm text-[#6B6B6B]">
              Tell us about your need and how we can provide the best solution for you
            </p>
          </div>

          <section className="max-h-[75vh] w-full flex flex-col gap-4 overflow-y-auto scrollbar-none">
            <FormInput
              label="Full name"
              placeholder="Abc Co-operative"
              register={register("fullName")}
              error={errors.fullName}
            />
            <FormInput
              label="Job Title"
              placeholder="Abc Co-operative"
              register={register("jobTitle")}
              error={errors.jobTitle}
            />
            <FormInput
              label="Company Name / Employer"
              placeholder="Abc Co-operative"
              register={register("company")}
              error={errors.company}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormInput
                label="Email Address"
                type="email"
                placeholder="abc@gmail.co"
                register={register("email")}
                error={errors.email}
              />
              <FormInput
                label="Phone number"
                placeholder="90908449840"
                register={register("phone")}
                error={errors.phone}
              />
            </div>
            <Controller
              name="cohort"
              control={control}
              render={({ field, fieldState }) => (
                <FormSelect
                  label="Preferred Cohort"
                  placeholder="Select an option"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  options={cohortOptions}
                  error={fieldState.error}
                />
              )}
            />
            <FormTextarea
              label="Additional Information"
              placeholder="Write your message"
              register={register("additional")}
              error={errors.additional}
            />
          </section>

          {submitError && (
            <p className="text-sm text-red-500">{submitError}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="h-12 px-8 rounded-full bg-[#CBA135] text-[#1A1A2E] font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-60 w-fit"
          >
            {loading ? "Sending..." : "Send Request"}
          </button>
        </form>
      )}
    </Modal>
  );
};

export default IndividualTrainingModal;
